import { store } from '../state';
import {
  getFiltersConfig,
  getFilterValue,
  getFilterOptions,
  isFilterLoading,
  setFilterValue,
  onFilterValueChanged
} from '../filters';
import type { FilterConfig, FilterOption } from '../filters';

const els = {
  filtersContainer: document.getElementById('filters-container') as HTMLElement,
  filtersToggleBtn: document.getElementById('filters-toggle-btn') as HTMLButtonElement
};

// Состояние панели фильтров
let isFiltersVisible = false;
let lastRenderState = '';

// ============================================================================
// Рендеринг панели фильтров
// ============================================================================

export function renderFiltersPanel(): void {
  const filtersConfig = getFiltersConfig();
  if (filtersConfig.length === 0) {
    els.filtersContainer.style.display = 'none';
    els.filtersToggleBtn.style.display = 'none';
    return;
  }

  // Показываем кнопку переключения
  els.filtersToggleBtn.style.display = 'flex';

  // Если панель скрыта — не рендерим содержимое
  if (!isFiltersVisible) {
    els.filtersContainer.style.display = 'none';
    return;
  }

  // Собираем текущее состояние для проверки изменений
  const state = store.getState().filters;
  const currentState = JSON.stringify({
    values: state.values,
    options: state.options,
    loading: state.loading
  });

  // Если состояние не изменилось — не перерисовываем
  if (currentState === lastRenderState) return;
  lastRenderState = currentState;

  els.filtersContainer.style.display = 'block';

  let html = '<div class="filters-panel">';
  for (const filter of filtersConfig) {
    html += renderFilter(filter);
  }
  html += '</div>';
  els.filtersContainer.innerHTML = html;

  attachEventListeners();
}

// ============================================================================
// Рендеринг одного фильтра
// ============================================================================

function renderFilter(filter: FilterConfig): string {
  const value = getFilterValue(filter.id);
  const options = getFilterOptions(filter.id);
  const loading = isFilterLoading(filter.id);

  const disabledAttr = loading ? 'disabled' : '';
  const loadingClass = loading ? 'filter-loading' : '';

  let html = `
    <div class="filter-item ${loadingClass}" data-filter-id="${filter.id}">
      <label class="filter-label">${escapeHtml(filter.label)}</label>
  `;

  if (filter.type === 'single-select') {
    html += renderSingleSelect(filter.id, value as string | null, options, disabledAttr);
  } else if (filter.type === 'multiselect') {
    html += renderMultiselect(filter.id, value as string[] | null, options, disabledAttr);
  }

  if (loading) {
    html += '<span class="filter-spinner"></span>';
  }

  html += '</div>';
  return html;
}

// ============================================================================
// Рендеринг single-select
// ============================================================================

function renderSingleSelect(
  filterId: string,
  value: string | null,
  options: FilterOption[],
  disabledAttr: string
): string {
  let html = `<select class="filter-select" data-filter-id="${filterId}" ${disabledAttr}>`;
  html += `<option value="">-- Выберите --</option>`;

  for (const opt of options) {
    const selected = value === opt.value ? 'selected' : '';
    html += `<option value="${escapeHtml(opt.value)}" ${selected}>${escapeHtml(opt.label)}</option>`;
  }

  html += '</select>';
  return html;
}

// ============================================================================
// Рендеринг multiselect
// ============================================================================

function renderMultiselect(
  filterId: string,
  value: string[] | null,
  options: FilterOption[],
  disabledAttr: string
): string {
  const selectedValues = value ?? [];
  const selectedCount = selectedValues.length;
  const displayText = selectedCount > 0 ? `Выбрано: ${selectedCount}` : '-- Выберите --';

  let html = `
    <div class="filter-multiselect" data-filter-id="${filterId}">
      <div class="filter-multiselect-toggle" ${disabledAttr}>
        <span class="filter-multiselect-text">${displayText}</span>
        <span class="filter-multiselect-arrow">▼</span>
      </div>
      <div class="filter-multiselect-dropdown" style="display: none;">
  `;

  for (const opt of options) {
    const checked = selectedValues.includes(opt.value) ? 'checked' : '';
    html += `
      <label class="filter-multiselect-option">
        <input type="checkbox" value="${escapeHtml(opt.value)}" ${checked}>
        <span>${escapeHtml(opt.label)}</span>
      </label>
    `;
  }

  html += '</div></div>';
  return html;
}

// ============================================================================
// Привязка обработчиков событий
// ============================================================================

function attachEventListeners(): void {
  // Single-select: изменение значения
  els.filtersContainer.querySelectorAll('.filter-select').forEach((select) => {
    select.addEventListener('change', (e) => {
      const target = e.target as HTMLSelectElement;
      const filterId = target.dataset.filterId!;
      const value = target.value || null;

      setFilterValue(filterId, value);
      onFilterValueChanged(filterId);
    });
  });

  // Multiselect: открытие/закрытие dropdown
  els.filtersContainer.querySelectorAll('.filter-multiselect-toggle').forEach((toggle) => {
    toggle.addEventListener('click', (e) => {
      const target = e.currentTarget as HTMLElement;
      if (target.hasAttribute('disabled')) return;

      const dropdown = target.nextElementSibling as HTMLElement;
      const isVisible = dropdown.style.display !== 'none';
      dropdown.style.display = isVisible ? 'none' : 'block';

      // Закрываем другие dropdown
      els.filtersContainer.querySelectorAll('.filter-multiselect-dropdown').forEach((dd) => {
        if (dd !== dropdown) {
          (dd as HTMLElement).style.display = 'none';
        }
      });
    });
  });

  // Multiselect: изменение чекбоксов
  els.filtersContainer.querySelectorAll('.filter-multiselect-option input').forEach((checkbox) => {
    checkbox.addEventListener('change', () => {
      const multiselect = checkbox.closest('.filter-multiselect') as HTMLElement;
      const filterId = multiselect.dataset.filterId!;
      const checkboxes = multiselect.querySelectorAll('input[type="checkbox"]') as NodeListOf<HTMLInputElement>;

      const selectedValues: string[] = [];
      checkboxes.forEach((cb) => {
        if (cb.checked) {
          selectedValues.push(cb.value);
        }
      });

      setFilterValue(filterId, selectedValues.length > 0 ? selectedValues : null);
      onFilterValueChanged(filterId);
    });
  });

  // Закрытие dropdown при клике вне
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.filter-multiselect')) {
      els.filtersContainer.querySelectorAll('.filter-multiselect-dropdown').forEach((dd) => {
        (dd as HTMLElement).style.display = 'none';
      });
    }
  });
}

// ============================================================================
// Переключение видимости панели фильтров
// ============================================================================

function toggleFiltersPanel(): void {
  isFiltersVisible = !isFiltersVisible;
  
  // Обновляем состояние кнопки
  if (isFiltersVisible) {
    els.filtersToggleBtn.classList.add('active');
  } else {
    els.filtersToggleBtn.classList.remove('active');
  }
  
  lastRenderState = '';

  // Перерисовываем панель
  renderFiltersPanel();
}

// ============================================================================
// Инициализация
// ============================================================================

export function initFiltersPanel(): void {
  console.log('🔧 Инициализация панели фильтров');
  
  // Обработчик кнопки переключения
  els.filtersToggleBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    toggleFiltersPanel();
  });
  
  // Подписка на изменения стейта фильтров
  store.subscribe(() => {
    renderFiltersPanel();
  });
  
  renderFiltersPanel();
}

// ============================================================================
// Вспомогательные функции
// ============================================================================

function escapeHtml(str: string): string {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}